import React, { useState } from "react";
import {
  CButton,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CSpinner,
} from "@coreui/react";
import { useAppSelector } from "../../../store/hooks";
import { MODAL_ACTION_CANCELLED } from "./setAutopayPaymentMethod";
import {
  formatOtherServiceAccountsCount,
  getServiceAccountScope,
} from "../../../utils/accountListDisplay";
import { ServiceAccountScopeAlert } from "../../../components/ServiceAccountScopeAlert";
import { ServiceAccountLabel } from "../../../components/ServiceAccountLabel";
import { PaymentMethodConfirmSummary } from "../../../components/PaymentMethodConfirmSummary";
import { PaymentMethodSourceLabel } from "../../PaymentMethods/components/PaymentMethodSourceLabel";

interface PropsInterface {
  paymentMethod?: {
    providerName: string;
    last4: string;
    type: string;
    expiryDate?: string | null;
    status?: string | null;
  };
  tenantAccountId?: string;
  resolve: () => void | Promise<void>;
  reject: (reason?: unknown) => void;
}

const ChangePaymentConfirmModal: React.FC<PropsInterface> = (props) => {
  const { paymentMethod, tenantAccountId, resolve, reject } = props;
  const [isSubmitting, setSubmitting] = useState(false);
  const { data, activeTenantAccountIndex, activeTenantAccountId } =
    useAppSelector((state) => state.accountSummary);
  const targetAccount =
    (tenantAccountId
      ? data?.find((account) => account.id === tenantAccountId)
      : data?.[activeTenantAccountIndex]) ?? data?.[activeTenantAccountIndex];
  const hasMultipleServiceAccounts = (data ?? []).length > 1;
  const scope = getServiceAccountScope(
    targetAccount ? [targetAccount] : [],
    activeTenantAccountId
  );
  const otherAutopayEnabledAccounts = (data ?? []).filter(
    (account) =>
      account.id !== targetAccount?.id && Boolean(account.autopay?.enabled)
  );

  return (
    <CModal
      show
      onClose={() => {
        if (!isSubmitting) {
          reject(MODAL_ACTION_CANCELLED);
        }
      }}
    >
      <CModalHeader>
        <strong>Change AutoPay Payment Method</strong>
      </CModalHeader>
      <CModalBody>
        {hasMultipleServiceAccounts && (
          <ServiceAccountScopeAlert scope={scope} />
        )}
        <p className="mb-3">
          Are you sure you want to change the AutoPay payment method for this
          service account?
        </p>
        <PaymentMethodConfirmSummary
          sections={[
            {
              label: "Service account",
              content: targetAccount ? (
                <ServiceAccountLabel
                  account={targetAccount}
                  className="modal-account-list-item"
                />
              ) : (
                <span>This service account</span>
              ),
            },
            {
              label: "New AutoPay payment method",
              content: paymentMethod ? (
                <PaymentMethodSourceLabel paymentMethod={paymentMethod} />
              ) : (
                <span>Payment method unavailable</span>
              ),
            },
          ]}
        />
        {otherAutopayEnabledAccounts.length > 0 && (
          <p className="text-muted mb-0">
            {formatOtherServiceAccountsCount(otherAutopayEnabledAccounts.length)}{" "}
            enrolled in AutoPay will keep their current payment method.
            {!tenantAccountId && (
              <>
                {" "}
                To change AutoPay for another service account, select that
                account from the account switcher and update its payment method
                individually.
              </>
            )}
          </p>
        )}
      </CModalBody>
      <CModalFooter style={{ display: "flex" }}>
        <CButton
          color="primary"
          style={{ flex: 2 }}
          disabled={isSubmitting}
          onClick={async () => {
            setSubmitting(true);
            try {
              await resolve();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {isSubmitting && (
            <CSpinner
              size="sm"
              style={{ marginRight: 8, verticalAlign: "middle" }}
            />
          )}
          Change Payment Method
        </CButton>
        <CButton
          color="secondary"
          style={{ flex: 1 }}
          disabled={isSubmitting}
          onClick={() => reject(MODAL_ACTION_CANCELLED)}
        >
          Cancel
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default ChangePaymentConfirmModal;
